import { useEffect, useMemo, useState } from "react";
import PageBreadcrumbs from "../../layouts/PageBreadcrumbs.js";
import { SkeletonTable } from "../../components/Skeleton.js";
import { Link } from "react-router-dom";
import { api, ApiError } from "../../lib/api.js";
import { useTableState } from "../../lib/useTableState.js";
import TableToolbar from "../../components/TableToolbar.js";

type SupportData = Awaited<ReturnType<typeof api.supportConsole>>;
type SupportWorkspace = SupportData["workspaces"][number];

export default function AdminSupportConsole() {
  const [data, setData] = useState<SupportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    let cancelled = false;
    api.supportConsole()
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e) => { if (!cancelled) setErr(e instanceof ApiError ? e.message : String(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const filtered = useMemo(() => (data?.workspaces ?? []).filter((w) => {
    if (!filter) return true;
    const q = filter.toLowerCase();
    return w.slug.toLowerCase().includes(q) || w.workspaceName.toLowerCase().includes(q);
  }), [data, filter]);

  const tWorkspaces = useTableState(filtered as unknown as Array<Record<string, unknown>>, {
    searchFields: ["slug", "workspaceName", "status"],
    initialPageSize: 25
  });

  return (
    <div className="page">
      <PageBreadcrumbs />
      <header className="page-head">
        <div>
          <h1>Support console</h1>
          <p>Read-only workspace overview for support-safe diagnostics. Never exposes tenant secrets.</p>
        </div>
      </header>

      {err && <div className="alert alert-error">{err}</div>}
      {loading ? <SkeletonTable rows={5} columns={4} /> : (
        <>
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-body" style={{ padding: 12 }}>
              <input
                type="text"
                placeholder="Filter by slug or name…"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                style={{ width: "100%" }}
              />
            </div>
          </div>
          <TableToolbar
            search={tWorkspaces.search} setSearch={tWorkspaces.setSearch}
            page={tWorkspaces.page} setPage={tWorkspaces.setPage}
            pageSize={tWorkspaces.pageSize} setPageSize={tWorkspaces.setPageSize}
            totalPages={tWorkspaces.totalPages} totalRows={tWorkspaces.totalRows}
            hideExport
            searchPlaceholder="Search workspace name / slug…"
          />
          <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr><th>Workspace</th><th>Name</th><th>Status</th><th>Members</th><th>Last login</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr><td colSpan={6} className="soft">No workspaces match.</td></tr>
                ) : (tWorkspaces.paged as unknown as SupportWorkspace[]).map((w) => (
                  <tr key={w.id}>
                    <td><code>{w.slug}</code></td>
                    <td>{w.workspaceName}</td>
                    <td><span className="pill pill-muted">{w.status}</span></td>
                    <td>{w.memberCount}</td>
                    <td className="soft">{w.lastLoginAt ? new Date(w.lastLoginAt).toLocaleString() : "—"}</td>
                    <td>
                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                        <Link className="btn btn-sm" to={`/admin/workspaces/${encodeURIComponent(w.id)}`}>Open</Link>
                        <Link className="btn btn-sm" to={`/admin/audit?workspaceId=${encodeURIComponent(w.id)}`}>Audit</Link>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
